import React, { useState, useEffect } from "react";
import { styled } from '@mui/system';
import {
    Button,
    Card,
    CardContent,
    Typography,
    Checkbox,
    FormControlLabel,
    IconButton,
} from "@mui/material";
import CookieIcon from '@mui/icons-material/Cookie';
import globals from "../globals";
import BlueButton from "../components/blueButton";
import RedButton from "../components/redButton";
import { triggerGTMMEtaPixel } from "./gtm";

const COOKIE_KEY = "cookie_preferences";

const defaultPreferences = {
    essential: true,
    analytics: true,
    marketing: true,
};

const cookieLabelsBG = {
essential: "Необходими",
analytics: "Аналитични",
marketing: "Маркетингови",
};

const CheckedCheckbox = styled(Checkbox)(() => ({
    color: globals.colors.darkBlue, // default (unchecked) color
    '&.Mui-checked': {
        color: globals.colors.darkBlue, // checked color
    },
    '&:hover': {
        // boxShadow: '0 0 5px lightcoral', // light red shadow on hover
        backgroundColor: globals.colors.lightGray, // optional: remove hover background
    },
}));

export default function CookieBanner() {
    const [showBanner, setShowBanner] = useState(false);
    const [showPreferences, setShowPreferences] = useState(false);
    const [preferences, setPreferences] = useState(defaultPreferences);
    const [saved, setSaved] = useState(false);

    useEffect(() => {
        const stored = localStorage.getItem(COOKIE_KEY);
        if (!stored) {
            setShowBanner(true);
        } else {
            setPreferences(JSON.parse(stored));
            setSaved(true);
        }
    }, []);

    const savePreferences = (prefs) => {
        localStorage.setItem(COOKIE_KEY, JSON.stringify(prefs));
        setPreferences(prefs);
        setSaved(true);
        setShowBanner(false);
        setShowPreferences(false);
        triggerGTMMEtaPixel();
    };

    const acceptAll = () => {
        triggerGTMMEtaPixel();
        savePreferences({ ...defaultPreferences });
    };

    const togglePreference = (key) => {
        if (key === "essential") return;
        setPreferences((prev) => ({ ...prev, [key]: !prev[key] }));
    };

    return (
        <>
            {/* Cookie Banner */}
            {showBanner && !showPreferences && (
                <div style={{ position: "sticky", bottom: 0, zIndex: 1300 }}>
                    <Card sx={{ width: "100%", borderRadius: 0, boxShadow: 6 }}>
                        <CardContent>
                            <Typography gutterBottom sx={{ fontSize: 16, fontWeight: 'bold' }}>
                                eAcademiaBg използва бисквитки.
                            </Typography>
                            <Typography sx={{ fontSize: 12 }} gutterBottom>
                                eAcademiaBg Ви препоръчва да се съгласите с използването на функционалните бисквитки,
                                които включват бисквитки с маркетингови и аналитични цели, осигурени от нас и нашите партньори
                                – включително Google и Meta. Тези бисквитки служат за персонализиране на съдържанието и рекламите, както и
                                за анализиране на посещенията на сайта – информация, която ни помага да Ви показваме ресурси и оферти,
                                които биха Ви били полезни. <br />
                                Ако сте съгласни, моля потвърдете с клик върху бутона <strong>„Да, съгласен съм“</strong>.<br />
                                За да получите повече информация, моля натиснете <strong>„Повече информация“</strong> или посетете <a href="/cookies">„Политика за поверителност и бисквитки“</a>.
                                Настройките на бисквитките можете да редактирате по всяко време, като посетите „Настройки на бисквитките“ най-долу в сайта или натиснете тук.
                            </Typography>
                            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 20 }}>
                                <BlueButton onClick={() => setShowPreferences(true)} text={'Повече информация'} sx={{ backgroundColor: globals.colors.backgroundColor, color: globals.colors.textColor, '&:hover': { backgroundColor: globals.colors.lightGray, } }}
                                />
                                <BlueButton onClick={acceptAll} text={'Да, съгласявам се'} />
                            </div>
                        </CardContent>
                    </Card>
                </div>
            )}

            {showPreferences && (
                <div style={{ position: "sticky", bottom: 0, zIndex: 1300 }}>
                    <Card sx={{ width: "100%", borderRadius: 0, boxShadow: 6 }}>
                        <CardContent>
                            <Typography sx={{ color: globals.colors.textColor, fontSize: 16, fontWeight: 'bold' }} gutterBottom>
                                Настройки на бисквитките
                            </Typography>
                            <Typography sx={{ fontSize: 14 }}>
                                За да Ви предоставим най-доброто потребителско изживяване, ние от eAcademiaBg използваме сесийни бисквитки, както и технологии на трети страни (като Google Tag Manager и Meta Pixel). Те ни помагат да подобрим функционалността на сайта, да анализираме посещаемостта и да Ви показваме подходящо съдържание и реклами.
                                Можете да управлявате кои категории бисквитки желаете да разрешите. Несъгласието с някои от тях може да ограничи функционалности или да доведе до по-малко релевантно съдържание.
                            </Typography>
                            <Typography sx={{ fontSize: 16, fontWeight: 'bold', mt: 3 }}>
                                Категории бисквитки
                            </Typography>
                            <Typography sx={{ mt: 2, fontSize: 14, fontWeight: 'bold' }}>
                                Необходими (сесийни)
                            </Typography>
                            <Typography sx={{ fontSize: 14 }}>
                                Тези бисквитки са задължителни за работата на сайта. Те осигуряват базови функции като вход, навигация, запомняне на езикови и визуални настройки, както и сигурност. Създават се временно и се изтриват при затваряне на браузъра.
                            </Typography>
                            <Typography sx={{ mt: 2, fontSize: 14, fontWeight: 'bold' }}>
                                Аналитични
                            </Typography>
                            <Typography sx={{ fontSize: 14 }}>
                                Използваме аналитични технологии, като Google Tag Manager, за да разберем как потребителите използват сайта ни – кои страници се посещават най-често, колко време се прекарва на тях, кои бутони се натискат и др.
                                Събраната информация ни помага да подобряваме потребителското изживяване.
                            </Typography>
                            <Typography sx={{ mt: 2, fontSize: 14, fontWeight: 'bold' }}>
                                Маркетингови (рекламни)
                            </Typography>
                            <Typography sx={{ fontSize: 14, mb: 2 }}>
                                Тези технологии (например Meta Pixel) ни позволяват да показваме персонализирани реклами и оферти въз основа на Вашето поведение на сайта. Така рекламите, които виждате в Google, Facebook или други платформи, ще са по-подходящи за Вас.
                            </Typography>
                            {Object.entries(preferences).map(([key, value]) => (
                                <FormControlLabel
                                    key={key}
                                    control={
                                        <CheckedCheckbox
                                            checked={value}
                                            disabled={key === "essential"}
                                            onChange={() => togglePreference(key)}
                                        />
                                    }
                                    label={
                                        <>
                                            {cookieLabelsBG[key]} {key !== "essential" ? "бисквитки" : ""}
                                            {key === "essential" && (
                                                <Typography variant="caption" color="text.secondary">
                                                    {" "}(Задължителни)
                                                </Typography>
                                            )}
                                        </>
                                    }
                                />
                            ))}
                            <Typography sx={{ fontSize: 14, mt: 2 }}>
                                Можете да промените тези настройки по всяко време чрез „Настройки на бисквитките“ в долната част на сайта.
                                Подробности за това как обработваме личните Ви данни ще намерите в нашата <a href="/privacy-policy">„Политика“ за поверителност</a> и <a href="/cookies">„Политика за бисквитки“</a>.
                            </Typography>

                            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 20 }}>
                                <BlueButton onClick={acceptAll} text={'Приемам всички'} />
                                <RedButton text=' Запази настройките' onClick={() => savePreferences(preferences)} />
                            </div>
                        </CardContent>
                    </Card>
                </div>
            )}

            {/* Settings Icon */}
            {saved && (
                <IconButton
                    onClick={() => setShowPreferences(true)}
                    sx={{
                        position: "fixed",
                        bottom: 16,
                        right: 16,
                        backgroundColor: globals.colors.backgroundColor,
                        boxShadow: 3,
                        ":hover": { backgroundColor: globals.colors.lightPink },
                    }}
                >
                    <CookieIcon fontSize="large" />
                </IconButton>
            )}
        </>
    );
}